import React, {useEffect, useState} from 'react';
import {Button, DatePicker, Input, message, Select, Upload} from 'antd';
import {FilePdfOutlined, SaveOutlined} from '@ant-design/icons';
import axios from 'axios';
import type {UploadFile} from 'antd/es/upload/interface';
import {TaskData} from '../../../../types/TaskData';
import './MeasureOrderCreation.css';
import moment, {Moment} from "moment";
import LoadingModal from "../../../../components/loadingModal/LoadingModal";
import dayjs, {Dayjs} from "dayjs";
import {getConfig} from "../../../../config";
import {ViewType} from "../../../../types/ViewType";
import {useDirtyContext} from "../../../../context/DirtyContext";
import locale from "antd/es/date-picker/locale/de_DE";

const { Option } = Select;
const { Dragger } = Upload;
const { TextArea } = Input;
const { API_BASE_URL } = getConfig();

interface MeasureOrderCreationProps {
    data: TaskData[];
    viewType: ViewType;
    onRefreshTable?: () => void;
    onSave?: (data: any) => void;
}

const MeasureOrderCreation: React.FC<MeasureOrderCreationProps> = ({ data, viewType, onRefreshTable, onSave }) => {
    const [files, setFiles] = useState<Record<string, UploadFile[]>>({
        facade: [],
        systemPlan: [],
        auxSheets: []
    });
    const [section, setSection] = useState('');
    const [measureResource, setMeasureResource] = useState('Projektleiter');
    const [readyDate, setReadyDate] = useState<Dayjs | null>(null);
    const [comment, setComment] = useState('');
    const [loadingCount, setLoadingCount] = useState(0);
    const [saving, setsaving] = useState(false);
    const [isFormComplete, setIsFormComplete] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const { isDirty, setDirty } = useDirtyContext();

    useEffect(() => {
        if (data) {
            setSection(data[0].insideOutside || '');
            setReadyDate(data[0].measureDate ? dayjs(data[0].measureDate, 'YYYY-MM-DD') : null);
        }
    }, [data]);


    useEffect(() => {
        const isComplete =
            section !== '' &&
            measureResource !== '' &&
            readyDate !== null &&
            files.facade.length > 0 &&
            files.systemPlan.length > 0 &&
            files.auxSheets.length > 0
        setIsFormComplete(isComplete);
    }, [section, readyDate, files, comment, measureResource]);

    useEffect(() => {
        if (viewType === ViewType.CHANGE && data) {
            const taskId = data[0]?.id;
            if (!taskId) return;
            const fetchOrderDetails = async () => {
                try {
                    setLoadingCount(prev => prev + 1);
                    const response = await axios.get(
                        `${API_BASE_URL}/getSingleNetworkPlanOrder?taskId=${taskId}`
                    );
                    if (response.data) {
                        const { desiredDeliveryDate, comment, ressource, auxSheetsFilename, facadeFilename, systemPlanFilename } = response.data;
                        setComment(comment || "");
                        setMeasureResource(ressource)
                        setFiles(prevFiles => ({
                            ...prevFiles,
                            facade: facadeFilename ? [{ uid: facadeFilename, name: facadeFilename, path: `/uploads/${facadeFilename}` }] : [],
                            systemPlan: systemPlanFilename ? [{ uid: systemPlanFilename, name: systemPlanFilename, path: `/uploads/${systemPlanFilename}` }] : [],
                            auxSheets: auxSheetsFilename ? [{ uid: auxSheetsFilename, name: auxSheetsFilename, path: `/uploads/${auxSheetsFilename}` }] : []
                        }));
                        if (desiredDeliveryDate) {
                            const parsedDate = dayjs(desiredDeliveryDate, 'DD.MM.YYYY');
                            if (parsedDate.isValid()) {
                                setReadyDate(dayjs(parsedDate.toDate()));
                            } else {
                                console.error("Invalid desiredDeliveryDate format:", desiredDeliveryDate);
                            }
                        }
                    }
                } catch (error) {
                    message.error("Fehler beim Abrufen der Bestellung.");
                } finally {
                    setLoadingCount(prev => Math.max(0, prev - 1));
                }
            };
            fetchOrderDetails();
        }
    }, [viewType, data]);

    const handleFileChange = (fileList: UploadFile[], key: string) => {
        setFiles(prev => ({ ...prev, [key]: fileList }));
    };

    const handleInputChange = <T,>(setter: (val: T) => void, value: T) => {
        setter(value);
        setDirty(true);
    };

    const fileToBase64 = (file: UploadFile): Promise<string | null> => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => resolve(null);
            if (file.originFileObj) reader.readAsDataURL(file.originFileObj);
        });
    };

    const handleSave = async () => {
        setsaving(true);
        setDirty(false);
        try {
            const taskIds = data.map(task => task.id);
            const facadeFile = files.facade[0];
            const systemPlanFile = files.systemPlan[0];
            const auxSheetsFile = files.auxSheets[0];
            const facadePDF = facadeFile?.originFileObj ? await fileToBase64(facadeFile) : null;
            const systemPlanPDF = systemPlanFile?.originFileObj ? await fileToBase64(systemPlanFile) : null;
            const auxSheetsPDF = auxSheetsFile?.originFileObj ? await fileToBase64(auxSheetsFile) : null;
            const facadeFilename = facadeFile?.name || null;
            const systemPlanFilename = systemPlanFile?.name || null;
            const auxSheetsFilename = auxSheetsFile?.name || null;
            const formattedDate = readyDate ? readyDate.format('DD.MM.YYYY') : null;
            const payload = {
                taskIds,
                facadePDF,
                facadeFilename,
                systemPlanPDF,
                systemPlanFilename,
                auxSheetsPDF,
                auxSheetsFilename,
                measureResource: measureResource,
                measureReadyType: section,
                measureReadyDate: formattedDate,
                comment,
            };
            if (viewType === ViewType.NEW_EXTERNAL && onSave) {
                onSave(payload);
                setIsSaved(true);
                return;
            }
            if (viewType === ViewType.CHANGE)   {
                await axios.post(`${API_BASE_URL}/changeNetworkPlanMeasureOrder`, payload);
            } else  {
                await axios.post(`${API_BASE_URL}/createNewNetworkPlanMeasureOrder`, payload);
            }
            message.success('Messbeauftragung wurde erfolgreich erstellt.');
            if (onRefreshTable) {
                onRefreshTable();
            }
        } catch (error) {
            message.error('Messbeauftragung konnte nicht erstellt werden');
        } finally {
            setsaving(false);
            if (onRefreshTable) {
                onRefreshTable();
            }
        }
    };

    const renderDragger = (label: string, key: string, required = false) => (
        <div className="draggable-upload-section">
            <label>
                <strong>
                    {label}
                    {required && <span style={{ color: 'red' }}> *</span>}
                </strong>
            </label>
            <Dragger
                fileList={files[key]}
                accept="application/pdf"
                beforeUpload={(file) => {
                    if (file.type !== 'application/pdf') {
                        message.error(`${file.name} ist kein PDF-Dokument!`);
                        return Upload.LIST_IGNORE;
                    }
                    return false;
                }}
                onChange={({ fileList }) => handleFileChange(fileList, key)}
                onRemove={(file) => {
                    setFiles(prev => ({
                        ...prev,
                        [key]: prev[key].filter(f => f.uid !== file.uid)
                    }));
                }}
            >
                <p className="ant-upload-drag-icon">
                    <FilePdfOutlined />
                </p>
                <p className="ant-upload-text">PDF hochladen</p>
            </Dragger>
        </div>
    );

    return (
        <div className="container">
            <LoadingModal
                open={saving}
                text="Messbeauftragung wird erstellt..."
            />
            <LoadingModal
                open={loadingCount > 0}
                text="Daten werden geladen..."
            />
            {renderDragger("Fassaden- und Grundrisspläne mit Geländer-Typen gekennzeichnet", "facade", true)}
            {renderDragger("Systemplanungs-Vorlage ODER Systemplan", "systemPlan", true)}
            {renderDragger("Hilfsblätter (bspw. Aufmass Fertigtreppe / Gitterrost etc.)", "auxSheets", true)}
            <LoadingModal
                open={saving}
                text="Messbeauftragung wird erstellt..."
            />
            <div className="input-group">
                <label>
                    <strong>
                        Messbereit Abschnitt<span style={{color: 'red'}}> *</span>
                    </strong>
                </label>
                <Select
                    value={section}
                    disabled={true}
                    onChange={value => handleInputChange(setSection, value)}
                    placeholder="Select Section"
                >
                    <Option value="Innen">Innen</Option>
                    <Option value="Aussen">Aussen</Option>
                    <Option value="Umgebung">Umgebung</Option>
                </Select>
            </div>

            <div className="input-group">
                <label>
                    <strong>
                        Mess-Ressource<span style={{color: 'red'}}> *</span>
                    </strong>
                </label>
                <Select
                    value={measureResource}
                    disabled={false}
                    onChange={value => handleInputChange(setMeasureResource, value)}
                    placeholder="Select Ressource"
                >
                    <Option value="Projektleiter">Projektleiter</Option>
                    <Option value="Messer">Messer</Option>
                </Select>
            </div>

            <div className="input-group">
                <label>
                    <strong>
                        Messbereit ab<span style={{color: 'red'}}> *</span>
                    </strong>
                </label>
                <DatePicker
                    locale={locale}
                    value={readyDate}
                    onChange={(date) => handleInputChange(setReadyDate, date)}
                    placeholder="Auswahl Datum"
                    showToday
                    disabled={false}
                    format="DD.MM.YYYY"
                    disabledDate={(current) => current && current.isBefore(dayjs(), 'day')}
                />
            </div>

            <div className="input-group">
                <label>
                    <strong>
                        Kommentar
                    </strong>
                </label>
                <TextArea
                    rows={3}
                    placeholder="Kommentar"
                    value={comment}
                    onChange={e => handleInputChange(setComment, e.target.value)}
                />
            </div>
            <div style={{textAlign: 'right'}}>
                <Button
                    type="primary"
                    icon={<SaveOutlined/>}
                    onClick={handleSave}
                    loading={saving}
                    disabled={!isFormComplete || (viewType === ViewType.NEW_EXTERNAL && isSaved)}
                >
                    {viewType === ViewType.NEW_EXTERNAL
                        ? "Zwischenspeichern"
                        : viewType === ViewType.KORREX
                            ? "Korrex erstellen"
                            : viewType === ViewType.CHANGE
                                ? "Beauftragung anpassen"
                                : "Beauftragen"}
                </Button>
            </div>

        </div>
    );
};

export default MeasureOrderCreation;
